import { useRouter } from 'next/router'
import type { Dispatch, SetStateAction } from 'react'

import logger from '@/utils/logger'
import type { SeriesAttributes, SessionAttributes } from '@/server/models'

import CourseQ from '@/queries/CourseQ'
import { LIVE_HELP_TOPIC } from '@/queries/constants'
import SeriesQ from '@/queries/SeriesQ'
import SessionQ from '@/queries/SessionQ'
import TopicQ from '@/queries/TopicQ'
import { SQL, type SQLFragment } from '@/client/query'

export type FilterBy = 'session' | 'series' | null

export function useQueryFilterBy(): FilterBy {
  const router = useRouter()

  const {
    query: { filter },
  } = router

  if (filter === 'session' || filter === 'series') {
    return filter
  }
  return null
}

export function useUpdateSelectedFilterBy(
  setDisableFilter: Dispatch<SetStateAction<boolean>>
): (filter: FilterBy) => void {
  const router = useRouter()

  return function updateSelectedFilterBy(filter: FilterBy) {
    setDisableFilter(true)

    const query = {
      ...router.query,
    }

    query.filter = filter === null ? [] : filter

    router
      .replace({ query })
      .catch(err => {
        logger.error('Navigation failed', { err })
      })
      .finally(() => setDisableFilter(false))
  }
}

export const QUERY_TOPICS = 'topics'

export function useQueryTopics(): number[] {
  const router = useRouter()

  let topics: string[] = []

  const maybeTopics = router.query[QUERY_TOPICS]
  if (typeof maybeTopics !== 'undefined') {
    topics = Array.isArray(maybeTopics) ? maybeTopics : [maybeTopics]
  }

  return topics.map(item => Number.parseInt(`${item}`, 10) || 0)
}

export function useUpdateSelectedTopics(
  setDisableFilter: Dispatch<SetStateAction<boolean>>
): (selected: number[]) => void {
  const router = useRouter()

  return function updateSelectedTopics(selected: number[]) {
    setDisableFilter(true)
    const query = {
      ...router.query,
      topics: selected,
    }
    router
      .push({ query })
      .catch(err => {
        logger.error('Navigation failed', { err })
      })
      .finally(() => setDisableFilter(false))
  }
}

// By default, filters out Live Help sessions and World sessions
export function getSessionsQuery({
  courseId = null,
  topicIds = [],
  courseSlug = null,
  includeLiveHelp = false,
  userId,
}: {
  courseId?: number | SQLFragment<number> | null
  topicIds?: number[]
  courseSlug?: string | SQLFragment<string> | null
  includeLiveHelp?: boolean
  userId: number
}): SQLFragment<SessionAttributes> {
  return SQL`
        FROM ${SessionQ}
        INNER JOIN ${TopicQ} ON ${SessionQ.topic_id} = ${TopicQ.id}
        INNER JOIN ${CourseQ} c2 ON c2.id = ${TopicQ.course_id}
        LEFT JOIN ${SeriesQ} ON ${SessionQ.series_id} = ${SeriesQ.id}
        WHERE
            ${SessionQ.deleted_at} IS NULL
            AND ${SessionQ.getComputedEndDate()} > CURRENT_TIMESTAMP
            AND ${SessionQ.series_id} IS NULL
            AND ${SessionQ.userCanViewSession(userId)}
            ${includeLiveHelp ? SQL`` : SQL`AND ${TopicQ.name} != ${LIVE_HELP_TOPIC}`}
            ${courseSlug == null ? SQL`` : SQL`AND c2.slug = ${courseSlug}`}
            ${courseId == null ? SQL`` : SQL`AND ${TopicQ.course_id} = ${courseId}`}
            ${topicIds.length === 0 ? SQL`` : SQL`AND ${SessionQ.topic_id} = ANY(${topicIds})`}
      `
}

export function getSeriesQuery({
  courseId = null,
  topicIds = [],
  courseSlug = null,
  userId,
  isLoggedOut,
}: {
  courseId?: number | SQLFragment<number> | null
  topicIds?: number[]
  courseSlug?: string | SQLFragment<string> | null
  userId: number
  isLoggedOut: boolean
}): SQLFragment<SeriesAttributes> {
  return SQL`
        FROM ${SeriesQ}
        INNER JOIN ${SessionQ} ON ${SessionQ.id} = (SELECT ${
          SessionQ.id
        } ${SeriesQ.getNextSession()})
        INNER JOIN ${CourseQ} c2 ON c2.id = ${SeriesQ.course_id}
        WHERE
            ${SeriesQ.deleted_at} IS NULL
            AND (NOT ${SeriesQ.isClosed()})
            AND (NOT ${SeriesQ.isEnrollmentClosed()})
            AND (${SeriesQ.getResearchGroup()} IS NULL)
            AND ${SeriesQ.userCanViewSeries(userId, { disableBucketCheck: isLoggedOut })}
            ${courseId == null ? SQL`` : SQL`AND ${SeriesQ.course_id} = ${courseId}`}
            ${courseSlug == null ? SQL`` : SQL`AND c2.slug = ${courseSlug}`}
            ${
              topicIds.length === 0
                ? SQL``
                : SQL`AND EXISTS(
                            SELECT 1 ${SessionQ.where({
                              seriesId: SeriesQ.id,
                              topicId: SQL`ANY(${topicIds})`,
                            })}
                        )`
            }
    `
}
